import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Anchor, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGO_URL, TAGLINE } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(password);
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center bg-gradient-to-b from-sky-100 via-sky-50 to-white py-12 px-4">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-10 left-10 h-40 w-40 rounded-full bg-yellow-200/50 blur-3xl"></div>
        <div className="absolute bottom-10 right-10 h-60 w-60 rounded-full bg-blue-300/40 blur-3xl"></div>
        <svg
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          className="absolute bottom-0 left-0 w-full h-40"
        >
          <path
            d="M0,160 C320,260 720,60 1440,180 L1440,320 L0,320 Z"
            fill="#BAE6FD"
            fillOpacity="0.6"
          />
        </svg>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-[2rem] shadow-2xl shadow-blue-900/20 w-full max-w-md p-8 md:p-10 border border-blue-50"
        data-testid="admin-login-card"
      >
        <div className="flex flex-col items-center text-center">
          <img src={LOGO_URL} alt="Rolling River" className="h-20 w-20 object-contain" />
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#1E3A8A] mt-3">
            Admin Sign In
          </h1>
          <p className="text-sm text-slate-500 italic mt-1">{TAGLINE}</p>
        </div>

        <form className="mt-8 space-y-5" onSubmit={onSubmit}>
          <div className="space-y-2">
            <Label className="text-sm font-bold text-[#1E3A8A]">Password</Label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="admin-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoFocus
                className="pl-9 rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="admin-login-button"
            disabled={submitting}
            className="w-full bg-[#2F5FCB] hover:bg-[#254BA0] text-white rounded-full py-6 font-bold text-base hover:-translate-y-0.5 transition-all shadow-lg shadow-blue-900/20"
          >
            {submitting ? "Signing in…" : "Sign in"}
            <Anchor className="ml-2 h-4 w-4" />
          </Button>
        </form>

        <p className="text-xs text-slate-400 text-center mt-6">
          <a href="/" className="text-[#2F5FCB] font-bold hover:underline">← Return to RSVP page</a>
        </p>
      </motion.div>
    </div>
  );
}
